import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Navigation, LinkIcon, Copy, AlertTriangle, Package, Crosshair, Trash2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_SHORTCUTS = [
  { status: "en_route_pickup", label: "In viaggio verso la raccolta" },
  { status: "arrived_pickup", label: "Arrivato alla raccolta" },
  { status: "waiting_documents", label: "In attesa dei documenti" },
  { status: "collected", label: "Salma raccolta" },
  { status: "in_transit", label: "Partito · in transito" },
  { status: "police_control", label: "Fermato · controllo polizia" },
  { status: "arrived_country", label: "Arrivato nel paese di destinazione" },
  { status: "delivered", label: "Arrivato a destinazione" },
];

const INCIDENT_TYPES = [
  { value: "police_stop", label: "Controllo polizia" },
  { value: "document_inspection", label: "Controllo documenti" },
  { value: "accident", label: "Incidente stradale" },
  { value: "vehicle_breakdown", label: "Guasto veicolo" },
  { value: "traffic_delay", label: "Ritardo traffico" },
  { value: "border_delay", label: "Ritardo frontiera" },
  { value: "custom", label: "Altro" },
];

export function LogisticsTab({ order, onUpdated }) {
  const { user } = useAuth();
  const isStaff = ["admin", "support"].includes(user?.role);
  const canTrack = ["admin", "support", "partner", "driver"].includes(user?.role);
  const [gps, setGps] = useState({ last_location: order.last_location, pings: [] });
  const [load, setLoad] = useState(null);
  const [links, setLinks] = useState([]);
  const [incidents, setIncidents] = useState(order.incidents || []);
  const [expiry, setExpiry] = useState("24");
  const [customHours, setCustomHours] = useState("12");
  const [inc, setInc] = useState({ type: "border_delay", note: "" });
  const [coffins, setCoffins] = useState(order.coffin_count || 1);

  const loadAll = async () => {
    try {
      const { data } = await api.get(`/orders/${order.id}/gps`);
      setGps(data);
    } catch (e) { logError("logistics:gps", e); }
    try { const { data } = await api.get(`/orders/${order.id}/incidents`); setIncidents(data); } catch (e) { logError("logistics:incidents", e); }
    if (isStaff) {
      api.get(`/orders/${order.id}/tracking-links`).then(({ data }) => setLinks(data)).catch((e) => logError("logistics:tracking-links", e));
      if (order.vehicle_id) api.get(`/vehicles/${order.vehicle_id}/load`).then(({ data }) => setLoad(data)).catch((e) => logError("logistics:vehicle-load", e));
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [order.id, order.vehicle_id]);

  const sendPosition = () => {
    if (!navigator.geolocation) { toast.error("Geolocalizzazione non supportata"); return; }
    navigator.geolocation.getCurrentPosition(async (p) => {
      try {
        await api.post("/gps", { order_id: order.id, lat: p.coords.latitude, lng: p.coords.longitude });
        toast.success("Posizione inviata"); loadAll();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    }, () => toast.error("Impossibile ottenere la posizione"), { enableHighAccuracy: true, timeout: 10000 });
  };

  const genLink = async () => {
    const hours = expiry === "custom" ? Number(customHours || 12) : Number(expiry);
    try {
      await api.post(`/orders/${order.id}/tracking-link`, { expiry_hours: hours });
      toast.success("Link generato"); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/track-live/${token}`);
    toast.success("Link copiato");
  };
  const revoke = async (token) => {
    try { await api.delete(`/tracking-link/${token}`); toast.success("Revocato"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submitIncident = async () => {
    try {
      const { data } = await api.post(`/orders/${order.id}/incident`, inc);
      toast.success("Incidente segnalato"); setInc({ type: "border_delay", note: "" });
      setIncidents(data.incidents || []); onUpdated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCoffins = async () => {
    try {
      const { data } = await api.patch(`/orders/${order.id}/coffins`, { coffin_count: Number(coffins) });
      toast.success("Aggiornato"); onUpdated?.(data); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const incLabel = (v) => INCIDENT_TYPES.find((x) => x.value === v)?.label || v;

  const quickStatus = async (s) => {
    try {
      await api.post(`/orders/${order.id}/tracking`, { status: s.status, note: s.label });
      const { data } = await api.get(`/orders/${order.id}`);
      onUpdated?.(data);
      toast.success(`Stato aggiornato: ${s.label}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="logistics-tab">
      {/* Quick status shortcuts (driver / partner / staff) — updates the shared tracking timeline */}
      {canTrack && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="quick-status-panel">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-3"><Navigation size={18} /> Aggiornamento rapido stato</h3>
          <div className="flex flex-wrap gap-2">
            {STATUS_SHORTCUTS.map((s) => (
              <button key={s.status} onClick={() => quickStatus(s)} data-testid={`quick-status-${s.status}`}
                className="text-sm px-3 py-1.5 rounded-full border border-slate-200 text-slate-700 hover:border-blue-700 hover:text-blue-800 hover:bg-blue-50 transition-colors">
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Lo stato è visibile in tempo reale ad admin, azienda, cliente e autista.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
      {/* GPS */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Navigation size={18} /> Posizione GPS</h3>
          {canTrack && <Button size="sm" variant="outline" onClick={sendPosition} data-testid="gps-send-btn"><Crosshair size={14} className="mr-1" /> Invia posizione</Button>}
        </div>
        {gps.last_location ? (
          <div className="text-sm">
            <p className="flex items-center gap-1.5 text-slate-700"><MapPin size={14} className="text-blue-900" /> {gps.last_location.lat.toFixed(5)}, {gps.last_location.lng.toFixed(5)}</p>
            <p className="text-xs text-slate-400 mt-1">Aggiornato {formatDateTime(gps.last_location.at)}</p>
            <a className="text-xs text-blue-700 hover:underline" href={`https://www.openstreetmap.org/?mlat=${gps.last_location.lat}&mlon=${gps.last_location.lng}#map=13/${gps.last_location.lat}/${gps.last_location.lng}`} target="_blank" rel="noreferrer">Apri su mappa</a>
          </div>
        ) : <p className="text-sm text-slate-400">Nessuna posizione registrata.</p>}
        {gps.pings?.length > 0 && (
          <div className="max-h-40 overflow-y-auto space-y-1 border-t border-slate-100 pt-3">
            {gps.pings.slice(0, 10).map((p, i) => (
              <div key={p.id || p.timestamp || `ping-${i}`} className="text-xs text-slate-500 flex justify-between"><span>{p.lat.toFixed(4)}, {p.lng.toFixed(4)}</span><span>{formatDateTime(p.timestamp)}</span></div>
            ))}
          </div>
        )}
      </div>

      {/* Capacity */}
      {isStaff && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Package size={18} /> Capacità & feretri</h3>
          <div className="flex items-end gap-2">
            <div className="space-y-1.5"><Label>Feretri di questo ordine</Label>
              <Input type="number" min="1" value={coffins} onChange={(e) => setCoffins(e.target.value)} className="w-28" data-testid="coffin-count-input" /></div>
            <Button onClick={saveCoffins} className="bg-blue-900 hover:bg-blue-800" data-testid="coffin-save-btn">Salva</Button>
          </div>
          {order.vehicle_id && load && (
            <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid="vehicle-load">
              <div className="font-medium text-slate-700">Veicolo {load.plate}</div>
              <div className="flex gap-4 mt-1 text-slate-500">
                <span>Capacità: <b className="text-slate-800">{load.capacity_coffins}</b></span>
                <span>Occupati: <b className="text-slate-800">{load.used}</b></span>
                <span>Liberi: <b className={load.available > 0 ? "text-emerald-600" : "text-rose-600"}>{load.available}</b></span>
              </div>
              {load.orders?.length > 1 && <p className="text-xs text-slate-400 mt-2">{load.orders.length} ordini su questo veicolo (trasporto multiplo).</p>}
            </div>
          )}
        </div>
      )}

      {/* Tracking link */}
      {isStaff && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><LinkIcon size={18} /> Link tracciamento cliente</h3>
          <div className="flex items-end gap-2 flex-wrap">
            <div className="space-y-1.5"><Label>Scadenza</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger className="w-36" data-testid="link-expiry-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 ore</SelectItem>
                  <SelectItem value="48">48 ore</SelectItem>
                  <SelectItem value="custom">Personalizzato</SelectItem>
                </SelectContent>
              </Select></div>
            {expiry === "custom" && <div className="space-y-1.5"><Label>Ore</Label><Input type="number" min="1" value={customHours} onChange={(e) => setCustomHours(e.target.value)} className="w-24" data-testid="link-custom-hours" /></div>}
            <Button onClick={genLink} className="bg-blue-900 hover:bg-blue-800" data-testid="gen-link-btn">Genera</Button>
          </div>
          <div className="space-y-2">
            {links.length === 0 && <p className="text-sm text-slate-400">Nessun link attivo.</p>}
            {links.map((l) => (
              <div key={l.token} className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 p-2" data-testid="tracking-link-row">
                <span className="flex-1 truncate font-mono text-xs text-slate-500">/track-live/{l.token.slice(0, 10)}…</span>
                <span className="text-xs text-slate-400">scade {formatDateTime(l.expires_at)}</span>
                <button onClick={() => copyLink(l.token)} className="text-slate-500 hover:text-blue-800 p-1" data-testid="copy-link-btn"><Copy size={14} /></button>
                <button onClick={() => revoke(l.token)} className="text-slate-400 hover:text-rose-600 p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Incidents */}
      {canTrack && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500" /> Incidenti</h3>
          <div className="space-y-2">
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Select value={inc.type} onValueChange={(v) => setInc({ ...inc, type: v })}>
                <SelectTrigger data-testid="incident-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{INCIDENT_TYPES.map((it) => <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={inc.note} onChange={(e) => setInc({ ...inc, note: e.target.value })} data-testid="incident-note" /></div>
            <Button onClick={submitIncident} variant="outline" data-testid="incident-submit-btn"><Send size={14} className="mr-1.5" /> Segnala incidente</Button>
          </div>
          {incidents.length > 0 && (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              {incidents.slice().reverse().map((it, i) => (
                <div key={it.timestamp || `inc-${i}`} className="text-sm" data-testid="incident-row">
                  <span className="font-medium text-amber-700">{incLabel(it.type)}</span>
                  <span className="text-xs text-slate-400 ml-2">{formatDateTime(it.timestamp)} · {it.by_role}</span>
                  {it.note && <p className="text-xs text-slate-500">{it.note}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
